import { useEffect, useRef, useState } from 'react';
import {
  changeRandomCheckTarget,
  fetchExtractionRules,
  fetchRandomChecks,
  fetchReviewBatches,
  pinVersion,
  unpinVersion,
  type ExtractionRulesTypeView,
  type RandomChecksTypeView,
} from '../lib/api';
import { useActionError } from '../lib/action-error';
import { ask } from '../lib/ask';
import { formatShortDate } from '../lib/format-date';
import { InfoTip } from '../lib/info-tip';
import { PagerButtons } from '../lib/pager-buttons';
import { documentTypeLabel, labels, sortByDocumentType } from '../lib/labels';
import { RoleNote, useGate } from '../lib/session';

/** QA-98: versions on each page of a type's list, newest first. */
const VERSIONS_PER_PAGE = 5;

type LoadState = { status: 'loading' } | { status: 'error' } | { status: 'ok'; types: ExtractionRulesTypeView[] };

/**
 * `#extraction-rules`: the active-version table, each type's
 * version list, and the guarded Pin/unpin write — the console's only configuration surface. A
 * waiting major version (ADR 0016) rides the same Pin/unpin control: accepting is *unpin --to*,
 * declining is *pin* to the version already active, never a seventh write action.
 */
export function ExtractionRules() {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  // QA-98: the page of each type lives here, not in `TypeSection`, which a reload unmounts.
  const [pages, setPages] = useState<Record<string, number>>({});

  function reload() {
    setState({ status: 'loading' });
    fetchExtractionRules()
      .then((types) => setState({ status: 'ok', types: sortByDocumentType(types) }))
      .catch(() => setState({ status: 'error' }));
  }

  useEffect(reload, []);

  return (
    <div>
      <div className="page-head">
        <h2>{labels.pageTitles.extractionRules}</h2>
        <p className="page-head__sub">{labels.extractionRules.pinHelp}</p>
      </div>
      {state.status === 'loading' && <p className="notice">{labels.extractionRules.loading}</p>}
      {state.status === 'error' && (
        <p className="notice notice--crit" role="alert">
          {labels.extractionRules.loadFailed}
        </p>
      )}
      {state.status === 'ok' &&
        state.types.map((view) => (
          <TypeSection
            key={view.type}
            view={view}
            page={pages[view.type] ?? 0}
            onPage={(page) => setPages((all) => ({ ...all, [view.type]: page }))}
            onChanged={reload}
          />
        ))}
      <RandomChecks />
    </div>
  );
}

type RandomChecksLoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ok'; rows: RandomChecksTypeView[] };

/** "Random checks" (ADR 0019): a second block on the same screen,
 *  same route/screen count. *Change what gets checked* is lighter than Pin/unpin — no reason
 *  field, confirm + audit only. */
function RandomChecks() {
  const [state, setState] = useState<RandomChecksLoadState>({ status: 'loading' });
  const action = useActionError();
  const gate = useGate('administer');
  // QA-132: a send of random checks cannot work without Data Studio. A failed read shows no note.
  const [sendOff, setSendOff] = useState(false);

  useEffect(() => {
    fetchReviewBatches()
      .then((view) => setSendOff(view.send?.randomCheck === 'off'))
      .catch(() => {});
  }, []);

  function reload() {
    setState({ status: 'loading' });
    fetchRandomChecks()
      .then((rows) => setState({ status: 'ok', rows: sortByDocumentType(rows) }))
      .catch(() => setState({ status: 'error' }));
  }

  useEffect(reload, []);

  async function handleChange(row: RandomChecksTypeView) {
    const typeLabel = documentTypeLabel(row.type);
    const answers = await ask({
      ...labels.randomChecks.changeConfirm(typeLabel, row.targetPerWeek),
      fields: [
        {
          name: 'targetPerWeek',
          label: labels.randomChecks.changeLabel(typeLabel),
          kind: 'number',
          required: true,
          value: row.targetPerWeek?.toString() ?? '',
        },
      ],
      confirmLabel: labels.randomChecks.changeButton,
    });
    if (!answers) return;
    if (await action.run(() => changeRandomCheckTarget(row.type, Number(answers.targetPerWeek)))) reload();
  }

  return (
    <section className="panel">
      <div className="panel__head">
        <h3>
          {labels.randomChecks.heading}
          <InfoTip label={labels.randomChecks.heading} text={labels.randomChecks.headingTip} />
        </h3>
      </div>
      <p className="panel__sub">{labels.randomChecks.sub}</p>
      {sendOff && <p className="notice">{labels.randomChecks.needsDataStudio}</p>}
      <RoleNote action="administer" />
      {action.notice}
      {state.status === 'loading' && <p className="panel__sub">{labels.randomChecks.loading}</p>}
      {state.status === 'error' && (
        <p className="notice notice--crit" role="alert">
          {labels.randomChecks.loadFailed}
        </p>
      )}
      {state.status === 'ok' && (
        <div className="responsive-table">
          <table className="random-checks-table">
            <thead>
              <tr>
                <th>{labels.randomChecks.columns.type}</th>
                <th className="num">{labels.randomChecks.columns.perWeek}
                  <InfoTip label={labels.randomChecks.columns.perWeek} text={labels.randomChecks.columnTips.perWeek} />
                </th>
                <th className="num">{labels.randomChecks.columns.checkedLastWeek}
                  <InfoTip label={labels.randomChecks.columns.checkedLastWeek} text={labels.randomChecks.columnTips.checkedLastWeek} />
                </th>
                <th className="num">{labels.randomChecks.columns.ofArrivals}
                  <InfoTip label={labels.randomChecks.columns.ofArrivals} text={labels.randomChecks.columnTips.ofArrivals} />
                </th>
                <th className="num">{labels.randomChecks.columns.accuracy}
                  <InfoTip label={labels.randomChecks.columns.accuracy} text={labels.randomChecks.columnTips.accuracy} />
                </th>
                <th className="table-actions"><span className="visually-hidden">{labels.tableActions}</span></th>
              </tr>
            </thead>
            <tbody>
              {state.rows.map((row) => (
                <tr key={row.type}>
                  <td>{documentTypeLabel(row.type)}</td>
                  {/* `not set` is prose, so only a number is mono. */}
                  <td className={row.targetPerWeek === null ? 'num' : 'mono num'}>
                    {row.targetPerWeek ?? labels.randomChecks.notSet}
                  </td>
                  <td className="mono num">{row.checkedLastWeek}</td>
                  <td className="mono num">
                    {row.ofArrivalsPercent == null ? '—' : `${row.ofArrivalsPercent}%`}
                  </td>
                  <td className="mono num">
                    {row.accuracyPercent == null ? '—' : labels.randomChecks.accuracyCell(row.accuracyPercent, row.accuracySample)}
                  </td>
                  <td className="table-actions">
                    {/* Lighter than Pin/unpin — confirm and audit, no reason field — and still a
                        write that changes what the pipeline does, so its dialog carries the guard.
                        The button is quiet: five guard fills in one column turn it orange
                        (`design.md` § CTA voice). */}
                    <button type="button" className="btn" onClick={() => handleChange(row)} {...gate.props}>
                      {labels.randomChecks.changeButton}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function TypeSection({
  view,
  page: requestedPage,
  onPage,
  onChanged,
}: {
  view: ExtractionRulesTypeView;
  page: number;
  onPage: (page: number) => void;
  onChanged: () => void;
}) {
  const reasonField = { name: 'reason', label: labels.extractionRules.reasonLabel, required: true } as const;
  const action = useActionError();
  const gate = useGate('administer');
  const columns = labels.extractionRules.versionColumns;
  const heading = useRef<HTMLHeadingElement>(null);
  // The list can get shorter after a write, so a page past the end falls back to the last page.
  const lastPage = Math.max(0, Math.ceil(view.versions.length / VERSIONS_PER_PAGE) - 1);
  const page = Math.min(requestedPage, lastPage);
  const shown = view.versions.slice(page * VERSIONS_PER_PAGE, (page + 1) * VERSIONS_PER_PAGE);
  // The active row stays in view on every page. No entry is active when `activeVersion` is null or
  // names a version the registry dropped, and then there is no kept row.
  const active = view.versions.find((version) => version.active);
  const kept = active && !shown.includes(active) ? active : undefined;

  async function handlePin(version: string) {
    const answers = await ask({
      ...labels.extractionRules.pinConfirm(view.type, view.activeVersion ?? '—', version),
      fields: [reasonField],
      confirmLabel: labels.extractionRules.pin,
    });
    if (!answers) return;
    if (await action.run(() => pinVersion(view.type, version, answers.reason))) onChanged();
  }

  async function handleDecline(waiting: string, current: string) {
    const answers = await ask({
      ...labels.extractionRules.declineConfirm(view.type, current, waiting),
      fields: [reasonField],
    });
    if (!answers) return;
    if (await action.run(() => pinVersion(view.type, current, answers.reason))) onChanged();
  }

  async function handleUnpin(to?: string) {
    const answers = await ask({
      ...(to
        ? labels.extractionRules.acceptConfirm(view.type, view.activeVersion ?? '—', to)
        : labels.extractionRules.unpinConfirm(view.type)),
      fields: [reasonField],
      confirmLabel: to ? labels.extractionRules.acceptVersion(to) : labels.extractionRules.unpin,
    });
    if (!answers) return;
    if (await action.run(() => unpinVersion(view.type, answers.reason, to))) onChanged();
  }

  function versionRow(version: ExtractionRulesTypeView['versions'][number], isKept: boolean) {
    return (
      <tr key={version.version} className={isKept ? 'version-kept' : undefined}>
        <td className="mono" data-label={columns.version}>{version.version}</td>
        <td className="mono" data-label={columns.published}>{formatShortDate(version.publishedAt)}</td>
        <td data-label={columns.status}>
          {version.active && labels.extractionRules.activeMark}
          {isKept && <span className="version-kept__note"> · {labels.extractionRules.keptNote}</span>}
        </td>
        <td className="table-actions">
          {version.active ? (
            view.mode === 'pinned' ? (
              <button
                type="button"
                className="btn btn--guard"
                title={labels.extractionRules.pinHelp}
                onClick={() => handleUnpin()}
                {...gate.props}
              >
                {labels.extractionRules.unpin}
              </button>
            ) : null
          ) : (
            <button
              type="button"
              className="btn btn--guard"
              title={labels.extractionRules.pinHelp}
              onClick={() => handlePin(version.version)}
              {...gate.props}
            >
              {labels.extractionRules.pinVersion(version.version)}
            </button>
          )}
        </td>
      </tr>
    );
  }

  return (
    <section className="panel">
      <div className="panel__head">
        <h3 ref={heading} tabIndex={-1}>{documentTypeLabel(view.type)}</h3>
      </div>
      <p>
        {!active && `${labels.extractionRules.inUse}: ${view.activeVersion ?? labels.extractionRules.none} · `}
        {view.mode === 'auto'
          ? labels.extractionRules.modeAuto
          : view.activeVersion
            ? labels.extractionRules.modePinned(view.activeVersion)
            : labels.extractionRules.modePinnedNoVersion}{' '}
        · {labels.extractionRules.summaryChanged}:{' '}
        {view.changedAt ? formatShortDate(view.changedAt) : '—'}
        {view.changedBy ? ` ${labels.extractionRules.summaryBy} ${view.changedBy}` : ''}
      </p>

      <RoleNote action="administer" />
      {action.notice}

      {view.backfillWarning && (
        <p className="notice notice--warn">
          {labels.extractionRules.backfillWarning(
            view.backfillWarning.documentsInFlightOnCurrentVersion,
            view.backfillWarning.documentsLeftInRun,
          )}
        </p>
      )}

      {view.waitingVersion && (
        // A waiting major version is a block *inside* this type's panel, not a panel of its own
        // — one containment layer, so it takes a recessive fill and a rule rather than a second
        // border (`design.md` § What screens MUST share).
        <div className="subpanel--fill flow-md">
          <h4>{labels.extractionRules.waitingHeading}</h4>
          <p>{labels.extractionRules.waitingVersion(view.waitingVersion.version, formatShortDate(view.waitingVersion.since))}</p>
          <ul>
            {view.waitingVersion.changeList.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <div className="action-row">
            {/* ADR 0016: accepting is `unpin --to` and declining is `pin` to what is already
                active. Both ride the same guarded Pin/unpin write, never a seventh action. */}
            <button
              type="button"
              className="btn btn--guard"
              onClick={() => handleUnpin(view.waitingVersion!.version)}
              {...gate.props}
            >
              {labels.extractionRules.acceptVersion(view.waitingVersion.version)}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => view.activeVersion && handleDecline(view.waitingVersion!.version, view.activeVersion)}
              {...gate.props}
            >
              {labels.extractionRules.decline}
            </button>
          </div>
        </div>
      )}

      <div className="responsive-table flow-md">
        {/* No visible caption: it would repeat the first column head. The name is for a screen reader. */}
        <table className="grid-versions" aria-label={`${documentTypeLabel(view.type)}: ${labels.extractionRules.versionListHeading}`}>
          <thead>
            <tr>
              <th>{labels.extractionRules.versionColumns.version}</th>
              <th>{labels.extractionRules.versionColumns.published}</th>
              <th>{labels.extractionRules.versionColumns.status}</th>
              <th className="table-actions"><span className="visually-hidden">{labels.tableActions}</span></th>
            </tr>
          </thead>
          {kept && <tbody>{versionRow(kept, true)}</tbody>}
          <tbody>{shown.map((version) => versionRow(version, false))}</tbody>
        </table>
      </div>
      <PagerButtons
        canNewer={page > 0}
        canOlder={page < lastPage}
        onNewer={() => onPage(page - 1)}
        onOlder={() => onPage(page + 1)}
        focusTarget={heading}
      />
    </section>
  );
}
